import { useState, useEffect, useRef } from "react";
import { useFooter } from "../../contexts/FooterContext";
import HeroSection from "./components/HeroSection";
import VisionSection from "./components/VisionSection";
import ProblemsSection from "./components/ProblemsSection";
import SolutionsSection from "./components/SolutionsSection";
import CoursesSection from "./components/CoursesSection";
import RoadmapSection from "./components/RoadmapSection";
import ValuesSection from "./components/ValuesSection";
import AudienceSection from "./components/AudienceSection";
import CommitmentSection from "./components/CommitmentSection";
import CTASection from "./components/CTASection";
// @ts-ignore
import { useAutoSEO } from '../../hooks/useSEOFromRoute';

export default function AcademyPage() {
  // Auto fetch SEO from database based on route
  useAutoSEO();

  const [visibleSections, setVisibleSections] = useState({
    hero: false,
    vision: false,
    problems: false,
    solutions: false,
    courses: false,
    roadmap: false,
    values: false,
    audience: false,
    commitment: false,
    cta: false,
  });

  const heroRef = useRef<HTMLElement>(null);
  const visionRef = useRef<HTMLElement>(null);
  const problemsRef = useRef<HTMLElement>(null);
  const solutionsRef = useRef<HTMLElement>(null);
  const coursesRef = useRef<HTMLElement>(null);
  const roadmapRef = useRef<HTMLElement>(null);
  const valuesRef = useRef<HTMLElement>(null);
  const audienceRef = useRef<HTMLElement>(null);
  const commitmentRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLElement>(null);

  const { footerData } = useFooter();

  // Scroll handlers
  const scrollToRoadmap = () => {
    roadmapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToCourses = () => {
    coursesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: "0px 0px -80px 0px",
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.getAttribute("data-section");
          if (sectionName) {
            setVisibleSections((prev) => ({ ...prev, [sectionName]: true }));
          }
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    const refs = [
      { ref: heroRef, name: "hero" },
      { ref: visionRef, name: "vision" },
      { ref: problemsRef, name: "problems" },
      { ref: solutionsRef, name: "solutions" },
      { ref: coursesRef, name: "courses" },
      { ref: roadmapRef, name: "roadmap" },
      { ref: valuesRef, name: "values" },
      { ref: audienceRef, name: "audience" },
      { ref: commitmentRef, name: "commitment" },
      { ref: ctaRef, name: "cta" },
    ];

    refs.forEach(({ ref }) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <HeroSection 
        isVisible={visibleSections.hero} 
        sectionRef={heroRef}
        onScrollToRoadmap={scrollToRoadmap}
        onScrollToCourses={scrollToCourses}
      />
      <VisionSection isVisible={visibleSections.vision} sectionRef={visionRef} />
      <ProblemsSection isVisible={visibleSections.problems} sectionRef={problemsRef} />
      <SolutionsSection isVisible={visibleSections.solutions} sectionRef={solutionsRef} />
      <CoursesSection isVisible={visibleSections.courses} sectionRef={coursesRef} />
      <RoadmapSection isVisible={visibleSections.roadmap} sectionRef={roadmapRef} />
      <ValuesSection isVisible={visibleSections.values} sectionRef={valuesRef} />
      <AudienceSection isVisible={visibleSections.audience} sectionRef={audienceRef} />
      <CommitmentSection isVisible={visibleSections.commitment} sectionRef={commitmentRef} />
      <CTASection 
        isVisible={visibleSections.cta} 
        sectionRef={ctaRef} 
        phone={footerData?.footer?.contact?.phone}
      />
    </div>
  );
}

